import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useWishlist } from "../hooks/useWishlist";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { ConfirmModal } from "../components/common";

const WishlistPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { showSuccess, showError } = useToast();
  const {
    wishlistItems,
    loading,
    error,
    pagination,
    removeFromWishlist,
    fetchWishlist,
  } = useWishlist();

  const [removingItems, setRemovingItems] = useState<Set<string>>(new Set());
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removingCourse, setRemovingCourse] = useState<{ id: string; title: string } | null>(null);

  const handleRemoveFromWishlist = (
    courseId: string,
    courseTitle: string
  ) => {
    setRemovingCourse({ id: courseId, title: courseTitle });
    setShowRemoveModal(true);
  };

  const confirmRemoveFromWishlist = async () => {
    if (!removingCourse) return;
    setShowRemoveModal(false);

    const { id: courseId } = removingCourse;
    setRemovingItems((prev) => new Set(prev).add(courseId));

    try {
      const success = await removeFromWishlist(courseId);
      if (success) {
        showSuccess("Course removed from wishlist");
      }
    } catch (err) {
      showError("Failed to remove course from wishlist");
    } finally {
      setRemovingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(courseId);
        return newSet;
      });
      setRemovingCourse(null);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (pagination) {
      fetchWishlist(newPage, pagination.limit);
    }
  };

  // Redirect if not authenticated or is admin
  if (!isAuthenticated || !user) {
    return (
      <div className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Access Denied
            </h1>
            <p className="text-gray-600 mb-8">
              You must be logged in to view your wishlist.
            </p>
            <Link
              to="/signin"
              className="bg-cyan-600 text-white px-6 py-3 rounded-md hover:bg-cyan-700 transition-colors"
            >
              Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (user.role === "ADMIN") {
    return (
      <div className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Access Denied
            </h1>
            <p className="text-gray-600 mb-8">
              Wishlist feature is not available for admin users.
            </p>
            <Link
              to="/admin"
              className="bg-cyan-600 text-white px-6 py-3 rounded-md hover:bg-cyan-700 transition-colors"
            >
              Go to Admin Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Wishlist</h1>
          <p className="text-gray-600">
            Courses you've saved for later consideration
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-red-400 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && wishlistItems.length === 0 && (
          <div className="text-center py-12">
            <svg
              className="w-16 h-16 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Your wishlist is empty
            </h3>
            <p className="text-gray-600 mb-6">
              Start exploring courses and add them to your wishlist to keep
              track of your favorites.
            </p>
            <Link
              to="/courses"
              className="bg-cyan-600 text-white px-6 py-3 rounded-md hover:bg-cyan-700 transition-colors"
            >
              Browse Courses
            </Link>
          </div>
        )}

        {/* Wishlist Items */}
        {!loading && !error && wishlistItems.length > 0 && (
          <div className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {wishlistItems.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {/* University Header */}
                  <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {item.course.university.logoUrl ? (
                          <img
                            src={item.course.university.logoUrl}
                            alt={item.course.university.name}
                            className="w-10 h-10 rounded-full bg-white p-1 mr-3"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display =
                                "none";
                            }}
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center mr-3">
                            <span className="text-cyan-600 font-bold text-sm">
                              {item.course.university.name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="text-white text-sm font-medium">
                            {item.course.university.name}
                          </p>
                          <p className="text-cyan-100 text-xs">
                            {item.course.university.city},{" "}
                            {item.course.university.country}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          handleRemoveFromWishlist(
                            item.courseId,
                            item.course.title
                          )
                        }
                        disabled={removingItems.has(item.courseId)}
                        className="text-white hover:text-red-200 transition-colors disabled:opacity-50"
                        title="Remove from wishlist"
                      >
                        {removingItems.has(item.courseId) ? (
                          <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                        ) : (
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Course Content */}
                  <div className="p-6">
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {item.course.title}
                      </h3>
                      {item.course.description && (
                        <p className="text-gray-600 text-sm line-clamp-3">
                          {item.course.description}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Level:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {item.course.level.replace("_", " ")}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Duration:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {item.course.duration} years
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">
                          Study Mode:
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {item.course.studyMode.replace("_", " ")}
                        </span>
                      </div>
                      {item.course.tuitionFee && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-500">
                            Tuition Fee:
                          </span>
                          <span className="text-sm font-semibold text-green-600">
                            ${item.course.tuitionFee.toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-3">
                      <Link
                        to={`/courses/${item.course.id}`}
                        className="flex-1 bg-cyan-600 text-white text-center py-2 px-4 rounded-md hover:bg-cyan-700 transition-colors text-sm font-medium"
                      >
                        View Details
                      </Link>
                      <Link
                        to={`/apply/${item.course.id}`}
                        className="flex-1 border border-cyan-600 text-cyan-600 text-center py-2 px-4 rounded-md hover:bg-cyan-50 transition-colors text-sm font-medium"
                      >
                        Apply Now
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="mt-8 flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPrev}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNext}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{" "}
                      <span className="font-medium">
                        {(pagination.page - 1) * pagination.limit + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(
                          pagination.page * pagination.limit,
                          pagination.total
                        )}
                      </span>{" "}
                      of <span className="font-medium">{pagination.total}</span>{" "}
                      courses
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={!pagination.hasPrev}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={!pagination.hasNext}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <ConfirmModal
        isOpen={showRemoveModal}
        title="Remove from Wishlist"
        message={`Remove "${removingCourse?.title}" from your wishlist?`}
        confirmLabel="Remove"
        cancelLabel="Keep"
        onConfirm={confirmRemoveFromWishlist}
        onCancel={() => { setShowRemoveModal(false); setRemovingCourse(null); }}
        variant="warning"
      />
    </div>
  );
};

export default WishlistPage;
